import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Chip from '../../components/chip'
import Icon from '../../components/icon'
import {
  CHALLENGE_TYPE_ICON,
  CHALLENGE_TYPE_LABEL,
  CHALLENGE_TYPES,
  STAGE_STATUS_LABEL,
  stageStatus
} from '../../utils/constant'
import { checkPublicRoute, formatDateTime } from '../../utils/utils'

const CandidateStagesDetail = ({ data, contestId }) => {
  const navigate = useNavigate()

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  const handleViewCode = useCallback(
    (stage) => {
      const path = `${
        isPublicRoute ? '' : '/'
      }assessments/${contestId}/edit/stage/${stage?.stageId}/${
        stage?.type === CHALLENGE_TYPES.VIDEO_RESPONSE
          ? 'video-response-evaluate'
          : stage?.type === CHALLENGE_TYPES.MCQ
          ? 'mcq-evaluate'
          : stage?.type === CHALLENGE_TYPES.AI_SCREENING
          ? 'ai-screening-evaluate'
          : 'evaluate'
      }/${stage?.attemptId}`

      navigate(path, {
        state: {
          pathname: `${window.location.pathname?.toLowerCase()}${
            window.location.search
          }`
        }
      })
    },
    [navigate, contestId, isPublicRoute]
  )

  return (
    <Card sx={{ padding: '24px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {data?.length > 0 && (
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: '700',
              paddingBottom: '10px'
            }}
          >
            Assessment Stages
          </Typography>
        )}

        {data?.length > 0 ? (
          data?.map((stage, index) => (
            <Accordion
              key={stage?.attemptId || index}
              elevation={0}
              square
              sx={{
                border: '1px solid var(--grey-300)',
                boxShadow: 'none',
                borderRadius: '8px',
                '&.MuiAccordion-root': {
                  boxShadow: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: '10px', md: '0px' },
                    paddingRight: { xs: '5px', md: '20px' }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Box
                      sx={{
                        padding: '8px',
                        backgroundColor: 'var(--grey-100)',
                        borderRadius: '8px',
                        alignContent: 'center'
                      }}
                    >
                      <Icon
                        icon={
                          CHALLENGE_TYPE_ICON?.[stage?.type] ||
                          'mdi:code-braces'
                        }
                        fontSize={'25px'}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography fontSize={'16px'} fontWeight={800}>
                        {stage?.stage}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <Chip
                      label={`${
                        stage?.score !== null
                          ? `Score: ${stage?.score}/${stage?.outOfScore} `
                          : 'Score: N/A'
                      }`}
                      variant="outlined"
                      size="medium"
                      color={
                        stage?.score === null
                          ? 'default'
                          : stage?.score > 70
                          ? 'success'
                          : stage?.score > 40
                          ? 'warning'
                          : 'error'
                      }
                    />
                    {stage?.type !== CHALLENGE_TYPES.INTERVIEW &&
                      stage?.type !== CHALLENGE_TYPES.PLACEMENT_TALK && (
                        <Button
                          variant="contained"
                          size="small"
                          endIcon={
                            <Icon
                              icon="streamline:code-monitor-1-solid"
                              color="inherit"
                              style={{ fontSize: '14px' }}
                            />
                          }
                          onClick={() => handleViewCode(stage)}
                          sx={{
                            bgcolor: 'var(--primary-main)',
                            color: 'var(--primary-contrastText)',
                            '&:hover': {
                              bgcolor: 'var(--primary-dark)'
                            },
                            padding: '7px 10px',
                            borderRadius: '8px'
                          }}
                        >
                          Evaluate
                        </Button>
                      )}
                  </Box>
                </Box>
              </AccordionSummary>
              <Divider sx={{ width: '100%' }} />
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Type
                    </Typography>
                    <Chip
                      rounded
                      size="small"
                      label={CHALLENGE_TYPE_LABEL?.[stage?.type] || '-'}
                      color="default"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Status
                    </Typography>
                    <Chip
                      rounded
                      size="small"
                      label={STAGE_STATUS_LABEL?.[stage?.status] || '-'}
                      color={
                        stage?.status === stageStatus?.DISQUALIFY ||
                        stage?.status === stageStatus?.REJECTED
                          ? 'error'
                          : stage?.status === stageStatus?.IN_PROGRESS
                          ? 'warning'
                          : stage?.status === stageStatus?.FINISHED
                          ? 'success'
                          : stage?.status === stageStatus?.NO_SHOW
                          ? 'alert'
                          : 'default'
                      }
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Duration
                    </Typography>
                    <Typography fontSize="15px">
                      {stage?.duration ? `${stage?.duration} mins` : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Start Time
                    </Typography>
                    <Typography fontSize="15px">
                      {stage?.startTimeStamp
                        ? formatDateTime(stage?.startTimeStamp, false, false )
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>

                {stage?.results?.filter((i) => i?.challengeId).length > 0 && (
                  <>
                    <Divider sx={{ width: '100%', margin: '20px 0px' }} />
                    <Typography fontSize="16px" fontWeight={600} mb={1}>
                      Challenges ({stage?.results?.length || 0})
                    </Typography>
                  </>
                )}

                {stage?.results?.length > 0 &&
                  stage?.results?.map(
                    (challenge) =>
                      challenge?.challengeId && (
                        <Box
                          sx={{
                            border: '1px solid var(--grey-300)',
                            borderRadius: '8px',
                            marginTop: '10px',
                            padding: '18px'
                          }}
                          key={challenge?.challengeId}
                        >
                          <Grid container spacing={4}>
                            <Grid item xs={6} sm={4}>
                              <Typography
                                fontSize="13px"
                                fontWeight={600}
                                color="var(--grey-500)"
                              >
                                Challenge Name
                              </Typography>
                              <Typography fontSize="15px" fontWeight={'600'}>
                                {challenge?.challengeName || '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography
                                fontSize="13px"
                                fontWeight={600}
                                color="var(--grey-500)"
                                mb={1}
                              >
                                Score
                              </Typography>
                              <Typography fontSize="15px" fontWeight={'600'}>
                                {challenge?.score !== null
                                  ? ` ${challenge?.score}/${challenge?.outOfScore}`
                                  : ' N/A'}
                              </Typography>
                            </Grid>
                            {challenge?.duration !== null &&
                              challenge?.duration > 0 && (
                                <Grid item xs={6} sm={3}>
                                  <Typography
                                    fontSize="13px"
                                    fontWeight={600}
                                    color="var(--grey-500)"
                                    mb={1}
                                  >
                                    Duration
                                  </Typography>
                                  <Typography
                                    fontSize="15px"
                                    fontWeight={'600'}
                                  >
                                    {challenge?.duration !== null &&
                                    challenge?.duration > 0
                                      ? `${challenge?.duration} mins`
                                      : '-'}
                                  </Typography>
                                </Grid>
                              )}
                            {challenge?.languageLabel && (
                              <Grid item xs={6} sm={2}>
                                <Typography
                                  fontSize="13px"
                                  fontWeight={600}
                                  color="var(--grey-500)"
                                  mb={1}
                                >
                                  Language
                                </Typography>
                                <Typography fontSize="15px" fontWeight={'600'}>
                                  {challenge?.languageLabel || '-'}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )
                  )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box
            sx={{
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography>No stage data available</Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default memo(CandidateStagesDetail)
